import { useCallback, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Button, Typography } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import LegendItem from "components/charts/multi-line/legend-item"
import Tooltip from "components/material/tooltip"
import AddSeriesPopover from "components/custom-metrics-selector/add-series-popover"
import SortLabelWithTooltip from "components/sort-label-with-tooltip"
import { ASC, DESC, getNextSort, sortDirLabel } from "helpers/sorts"
import { MULTI_LINE_ITEM_SHAPE, ADD_SERIES_METRICS_SHAPE } from "../../prop-types"
import { LEGEND_SORT_COLUMNS, sortLegendItems } from "../../helpers"
import "./styles.scss"

const FIRST_SORT = {
  [LEGEND_SORT_COLUMNS.NAME]: { column: LEGEND_SORT_COLUMNS.NAME, direction: ASC },
  [LEGEND_SORT_COLUMNS.VALUE]: { column: LEGEND_SORT_COLUMNS.VALUE, direction: DESC, absolute: true },
}
const DEFAULT_SORT = FIRST_SORT[LEGEND_SORT_COLUMNS.NAME]

const DISABLED_ADD_SERIES_SX = {
  "&[aria-disabled='true']": { color: "text.disabled", cursor: "default", "&:hover": { backgroundColor: "transparent" } },
}

function getSortHeaderLabel(name, { isActive, direction, absolute, alphabetical = false }) {
  if (!isActive) return `Sort by ${name}`
  return `Sort by ${name} (${sortDirLabel({ dir: direction, abs: Boolean(absolute), alphabetical })})`
}

// Reuses LegendItem's handlers from the inline legend; addSeriesMetrics is omitted for a template with no metric family (Beta).
export default function LegendPanel({
  items,
  visibleCount,
  latestValues,
  latestDates,
  formatter,
  onlyLegendText,
  onClick,
  onOnlyToggle,
  onValueClick,
  focus,
  blur,
  focused,
  addSeriesMetrics,
  alreadyShownMetricIds,
  onAddSeries,
  addSeriesDisabledReason,
  sortable = true,
  // Surfaced here so a no-op-looking "+ Add series" pick still gives feedback.
  noDataAddedCount = 0,
}) {
  // Captured from the click event, not a ref read during render -- react-hooks/refs forbids that.
  const [addSeriesAnchorEl, setAddSeriesAnchorEl] = useState(null)
  const [sort, setSort] = useState(DEFAULT_SORT)

  const handleOpenAddSeries = useCallback((event) => setAddSeriesAnchorEl(event.currentTarget), [])
  const handleCloseAddSeries = useCallback(() => setAddSeriesAnchorEl(null), [])
  const handleSelectAddSeries = useCallback((metricId) => {
    onAddSeries?.(metricId)
    setAddSeriesAnchorEl(null)
  }, [onAddSeries])

  const handleSort = useCallback((column) => {
    setSort(previous => {
      if (previous.column !== column) return FIRST_SORT[column]
      return { column, ...getNextSort(previous, column === LEGEND_SORT_COLUMNS.VALUE) }
    })
  }, [])
  const handleSortByName = useCallback(() => handleSort(LEGEND_SORT_COLUMNS.NAME), [handleSort])
  const handleSortByValue = useCallback(() => handleSort(LEGEND_SORT_COLUMNS.VALUE), [handleSort])

  const isNameSorted = sort.column === LEGEND_SORT_COLUMNS.NAME
  const isValueSorted = sort.column === LEGEND_SORT_COLUMNS.VALUE

  const sortedItems = useMemo(
    () => (sortable ? sortLegendItems(items, latestValues, sort.column, sort.direction, sort.absolute) : items),
    [sortable, items, latestValues, sort.column, sort.direction, sort.absolute],
  )

  return (
    <div className="legend-panel">
      <div className="legend-panel-header" aria-live="polite">{visibleCount} of {items.length}</div>
      <div className="legend-panel-column-headers">
        {sortable
          ? (
            <>
              <SortLabelWithTooltip
                className="legend-panel-column-header legend-panel-column-header--name"
                alphabetical
                noSortLabels={!isNameSorted}
                active={isNameSorted}
                aria-label={getSortHeaderLabel("Name", { isActive: isNameSorted, direction: sort.direction, alphabetical: true })}
                direction={isNameSorted ? sort.direction : FIRST_SORT[LEGEND_SORT_COLUMNS.NAME].direction}
                onClick={handleSortByName}
              >
                Name
              </SortLabelWithTooltip>
              <SortLabelWithTooltip
                className="legend-panel-column-header legend-panel-column-header--value"
                noSortLabels={!isValueSorted}
                active={isValueSorted}
                absolute={isValueSorted && Boolean(sort.absolute)}
                aria-label={getSortHeaderLabel("Value", { isActive: isValueSorted, direction: sort.direction, absolute: sort.absolute })}
                direction={isValueSorted ? sort.direction : FIRST_SORT[LEGEND_SORT_COLUMNS.VALUE].direction}
                classes={{ icon: isValueSorted && sort.absolute ? "abs-order-icon" : undefined }}
                onClick={handleSortByValue}
              >
                Value
              </SortLabelWithTooltip>
            </>
          )
          : (
            <>
              <span className="legend-panel-column-header legend-panel-column-header--name">Name</span>
              <span className="legend-panel-column-header legend-panel-column-header--value">Value</span>
            </>
          )}
      </div>
      <div className="legend-panel-list">
        {sortedItems.map(item => (
          <LegendItem
            key={item.id}
            {...item}
            value={latestValues[item.id]}
            valueDate={latestDates?.[item.id]}
            dotPlacement="end"
            showValue
            formatter={formatter}
            onlyLegendText={onlyLegendText}
            onClick={onClick}
            onOnlyToggle={onOnlyToggle}
            onValueClick={onValueClick}
            focus={focus}
            blur={blur}
            focused={focused}
          />
        ))}
        {addSeriesMetrics && (
          <div className="legend-panel-add-series">
            <Button
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={handleOpenAddSeries}
            >
              Add series
            </Button>
            <AddSeriesPopover
              open={Boolean(addSeriesAnchorEl)}
              anchorEl={addSeriesAnchorEl}
              onClose={handleCloseAddSeries}
              availableMetricsGroups={addSeriesMetrics}
              alreadyShownMetricIds={alreadyShownMetricIds}
              onSelect={handleSelectAddSeries}
            />
          </div>
        )}
        {!addSeriesMetrics && addSeriesDisabledReason && (
          <div className="legend-panel-add-series">
            <Tooltip title={addSeriesDisabledReason} describeChild>
              <Button size="small" startIcon={<AddIcon fontSize="small" />} aria-disabled="true" sx={DISABLED_ADD_SERIES_SX}>
                Add series
              </Button>
            </Tooltip>
          </div>
        )}
      </div>
      {noDataAddedCount > 0 && (
        <Typography variant="caption" color="text.secondary" className="legend-panel-no-data-notice">
          {noDataAddedCount === 1
            ? "1 added series has no data for this period"
            : `${noDataAddedCount} added series have no data for this period`}
        </Typography>
      )}
    </div>
  )
}

LegendPanel.propTypes = {
  items: PropTypes.arrayOf(MULTI_LINE_ITEM_SHAPE).isRequired,
  visibleCount: PropTypes.number.isRequired,
  latestValues: PropTypes.objectOf(PropTypes.number).isRequired,
  latestDates: PropTypes.objectOf(PropTypes.string),
  formatter: PropTypes.func,
  onlyLegendText: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  onOnlyToggle: PropTypes.func.isRequired,
  // Optional: a template that hasn't wired legend drilldown just renders the value as plain text.
  onValueClick: PropTypes.func,
  focus: PropTypes.func.isRequired,
  blur: PropTypes.func.isRequired,
  focused: PropTypes.string,
  // All three travel together; omit addSeriesMetrics to hide the control.
  addSeriesMetrics: ADD_SERIES_METRICS_SHAPE,
  alreadyShownMetricIds: PropTypes.instanceOf(Set),
  onAddSeries: PropTypes.func,
  noDataAddedCount: PropTypes.number,
  // For a view with no addable metric family: shows a disabled "Add series" with this hint instead of omitting it.
  addSeriesDisabledReason: PropTypes.string,
  // false keeps the caller's row order and renders plain Name/Value headers (a fixed-order table like Risk Total).
  sortable: PropTypes.bool,
}
